/**
 * Performance Monitor for debugging freezing issues
 *
 * Usage in browser console:
 *   window.perfMon.mark('x6-throttle-start')
 *   window.perfMon.mark('router-connected')
 *   window.perfMon.mark('router-disconnected')
 *   window.perfMon.report()
 *   window.perfMon.reset()
 *   window.perfMon.startContinuousReport(5000) // Report every 5s
 *   window.perfMon.stopContinuousReport()
 */

class PerformanceMonitor {
  constructor() {
    this.markers = [];
    this.metrics = {
      // Frame metrics
      frameCount: 0,
      frameTimes: [],
      lastFrameTime: 0,

      // Router metrics
      accountDataEvents: 0,
      accountDataSkipped: 0,
      reportEvents: 0,

      // Store update metrics
      orderStoreUpdates: 0,
      positionStoreUpdates: 0,

      // Render metrics
      chartRenders: 0,
      indicatorCalculations: 0,

      // React metrics
      reactUpdates: 0,
    };

    this.startTime = performance.now();
    this.lastReportTime = performance.now();
    this.continuousReportInterval = null;

    // Start frame tracking
    this._trackFrames();

    // Expose to window for console access
    if (typeof window !== "undefined") {
      window.perfMon = this;
    }
  }

  _trackFrames() {
    const trackFrame = () => {
      const now = performance.now();
      if (this.lastFrameTime > 0) {
        const frameTime = now - this.lastFrameTime;
        this.metrics.frameTimes.push(frameTime);
        // Keep only last 60 frames for rolling average
        if (this.metrics.frameTimes.length > 60) {
          this.metrics.frameTimes.shift();
        }
      }
      this.lastFrameTime = now;
      this.metrics.frameCount++;
      requestAnimationFrame(trackFrame);
    };
    requestAnimationFrame(trackFrame);
  }

  // Add a marker with timestamp
  mark(label) {
    const now = performance.now();
    const elapsed = ((now - this.startTime) / 1000).toFixed(1);
    const marker = {
      label,
      time: now,
      elapsed: `${elapsed}s`,
      snapshot: this._getSnapshot(),
    };
    this.markers.push(marker);

    console.log(`\n🚩 ===== MARKER: ${label} (at ${elapsed}s) =====`);
    console.log("📊 Current metrics:", marker.snapshot);
    console.log("=====================================\n");

    return marker;
  }

  // Get current metrics snapshot
  _getSnapshot() {
    const frameTimes = this.metrics.frameTimes;
    const avgFrameTime =
      frameTimes.length > 0
        ? frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length
        : 0;
    const fps = avgFrameTime > 0 ? 1000 / avgFrameTime : 0;

    // Calculate frame time percentiles
    const sorted = [...frameTimes].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)] || 0;
    const p99 = sorted[Math.floor(sorted.length * 0.99)] || 0;
    const maxFrame = Math.max(...frameTimes, 0);

    return {
      fps: fps.toFixed(1),
      avgFrameMs: avgFrameTime.toFixed(1),
      p95FrameMs: p95.toFixed(1),
      p99FrameMs: p99.toFixed(1),
      maxFrameMs: maxFrame.toFixed(1),
      accountDataEvents: this.metrics.accountDataEvents,
      accountDataSkipped: this.metrics.accountDataSkipped,
      skipRate:
        this.metrics.accountDataEvents > 0
          ? (
              (this.metrics.accountDataSkipped /
                this.metrics.accountDataEvents) *
              100
            ).toFixed(0) + "%"
          : "0%",
      reportEvents: this.metrics.reportEvents,
      orderStoreUpdates: this.metrics.orderStoreUpdates,
      positionStoreUpdates: this.metrics.positionStoreUpdates,
      chartRenders: this.metrics.chartRenders,
      indicatorCalculations: this.metrics.indicatorCalculations,
    };
  }

  // Track specific events
  trackAccountData(skipped = false) {
    this.metrics.accountDataEvents++;
    if (skipped) this.metrics.accountDataSkipped++;
  }

  trackReport() {
    this.metrics.reportEvents++;
  }

  trackOrderStoreUpdate() {
    this.metrics.orderStoreUpdates++;
  }

  trackPositionStoreUpdate() {
    this.metrics.positionStoreUpdates++;
  }

  trackChartRender() {
    this.metrics.chartRenders++;
  }

  trackIndicatorCalculation() {
    this.metrics.indicatorCalculations++;
  }

  // Generate report
  report() {
    const now = performance.now();
    const totalElapsed = (now - this.startTime) / 1000;
    const sinceLastReport = (now - this.lastReportTime) / 1000;
    this.lastReportTime = now;

    const snapshot = this._getSnapshot();

    console.log("\n📈 ===== PERFORMANCE REPORT =====");
    console.log(
      `⏱️  Total time: ${totalElapsed.toFixed(1)}s | Since last report: ${sinceLastReport.toFixed(1)}s`,
    );
    console.log("");
    console.log("🖼️  FRAME METRICS:");
    console.log(
      `    FPS: ${snapshot.fps} | Avg: ${snapshot.avgFrameMs}ms | P95: ${snapshot.p95FrameMs}ms | P99: ${snapshot.p99FrameMs}ms | Max: ${snapshot.maxFrameMs}ms`,
    );
    console.log("");
    console.log("📡 ROUTER METRICS:");
    console.log(
      `    Account Data: ${snapshot.accountDataEvents} events | Skipped: ${snapshot.accountDataSkipped} (${snapshot.skipRate})`,
    );
    console.log(`    Reports: ${snapshot.reportEvents}`);
    console.log("");
    console.log("🗄️  STORE METRICS:");
    console.log(`    Order Store Updates: ${snapshot.orderStoreUpdates}`);
    console.log(`    Position Store Updates: ${snapshot.positionStoreUpdates}`);
    console.log("");
    console.log("📊 CHART METRICS:");
    console.log(`    Chart Renders: ${snapshot.chartRenders}`);
    console.log(
      `    Indicator Calculations: ${snapshot.indicatorCalculations}`,
    );
    console.log("");

    if (this.markers.length > 0) {
      console.log("🚩 MARKERS:");
      this.markers.forEach((m, i) => {
        console.log(`    ${i + 1}. [${m.elapsed}] ${m.label}`);
      });
      console.log("");
    }

    console.log("=================================\n");

    return snapshot;
  }

  // Start continuous reporting
  startContinuousReport(intervalMs = 5000) {
    if (this.continuousReportInterval) {
      clearInterval(this.continuousReportInterval);
    }
    console.log(`📊 Starting continuous report every ${intervalMs}ms`);
    this.continuousReportInterval = setInterval(
      () => this.report(),
      intervalMs,
    );
  }

  // Stop continuous reporting
  stopContinuousReport() {
    if (this.continuousReportInterval) {
      clearInterval(this.continuousReportInterval);
      this.continuousReportInterval = null;
      console.log("📊 Stopped continuous report");
    }
  }

  // Reset all metrics
  reset() {
    this.markers = [];
    this.metrics = {
      frameCount: 0,
      frameTimes: [],
      lastFrameTime: 0,
      accountDataEvents: 0,
      accountDataSkipped: 0,
      reportEvents: 0,
      orderStoreUpdates: 0,
      positionStoreUpdates: 0,
      chartRenders: 0,
      indicatorCalculations: 0,
      reactUpdates: 0,
    };
    this.startTime = performance.now();
    this.lastReportTime = performance.now();
    console.log("📊 Performance monitor reset");
  }

  // Compare two markers
  compare(marker1Label, marker2Label) {
    const m1 = this.markers.find((m) => m.label === marker1Label);
    const m2 = this.markers.find((m) => m.label === marker2Label);

    if (!m1 || !m2) {
      console.log("❌ Markers not found");
      return null;
    }

    console.log(`\n📊 COMPARISON: "${marker1Label}" vs "${marker2Label}"`);
    console.log("─".repeat(50));

    const s1 = m1.snapshot;
    const s2 = m2.snapshot;

    const compare = (label, v1, v2, unit = "") => {
      const diff = parseFloat(v2) - parseFloat(v1);
      const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
      const color = diff > 0 ? "🔴" : diff < 0 ? "🟢" : "⚪";
      console.log(
        `  ${label}: ${v1}${unit} → ${v2}${unit} ${color}${arrow} ${Math.abs(diff).toFixed(1)}${unit}`,
      );
    };

    compare("FPS", s1.fps, s2.fps);
    compare("Avg Frame", s1.avgFrameMs, s2.avgFrameMs, "ms");
    compare("P95 Frame", s1.p95FrameMs, s2.p95FrameMs, "ms");
    compare("Max Frame", s1.maxFrameMs, s2.maxFrameMs, "ms");

    console.log("─".repeat(50) + "\n");
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;
